import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { map, Observable, Subscription } from 'rxjs';
import { ProductReference } from 'src/app/models/layout/product-reference';
import { ProductDimensions } from 'src/app/models/layout/product-dimensions';
import { LayoutService } from 'src/app/services/layout.service';
import { PlanogramService } from 'src/app/services/planogram.service';
import { ProductInspectorService } from 'src/app/services/product-inspector.service';
import { ZoomService } from 'src/app/services/zoom.service';
import { DragContextService } from 'src/app/services/dragcontext.service';
import { ContextMenuService } from 'src/app/services/contextmenu.service';
import { ProductBinService } from 'src/app/services/productbin.service';
import { ProductStatus } from 'src/app/models/layout/product-status';
import { ImageComponent } from '../../../../image/image.component';
import { AsyncPipe } from '@angular/common';
import { CapabilitiesService } from 'src/app/services/capabilities.service';
import { CapabilityType } from 'src/app/models/capability-type';

@Component({
    selector: 'app-product-bin-product',
    templateUrl: './product-bin-product.component.html',
    styleUrls: ['./product-bin-product.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ImageComponent, AsyncPipe]
})
export class ProductBinProductComponent implements OnInit, OnDestroy {
  @Input() productRef: ProductReference | null = null;
  @Input() depth = 0;
  @Input() isContainer = false;
  readonly ProductStatus = ProductStatus;
  isInspecting = false;
  private subscriptions = new Subscription();

  constructor(
    private zoomService: ZoomService,
    private layoutService: LayoutService,
    private productInspectorService: ProductInspectorService,
    private elementRef: ElementRef,
    private dragContextService: DragContextService,
    private contextMenuService: ContextMenuService,
    private productBinService: ProductBinService,
    private capabilitiesService: CapabilitiesService,
    private cdr: ChangeDetectorRef
  ) { }

  // Products can only be dragged out of the bin onto the planogram when editing is allowed
  // (false for linked/view-only planograms and VIEW shares).
  get canEdit(): boolean {
    return this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM);
  }

  get hasContainers(): boolean {
    return this.productRef != null && this.productBinService.hasContainers(this.productRef.productId);
  }

  get directContainers(): ProductReference[] {
    return this.productRef ? this.productBinService.getDirectContainers(this.productRef.productId) : [];
  }

  get isExpanded(): boolean {
    return this.productRef != null && this.productBinService.isContainerExpanded(this.productRef.productId);
  }

  get totalBaseUnits(): number {
    return this.productRef ? this.productBinService.getTotalBaseUnits(this.productRef) : 1;
  }

  get rowIndentStyle() {
    return { 'padding-left.px': 16 + this.depth * 20 };
  }

  toggleContainer(event: MouseEvent) {
    event.stopPropagation();
    if (this.productRef) {
      this.productBinService.toggleContainerExpansion(this.productRef.productId);
      this.cdr.markForCheck();
    }
  }

  onProductRightClick(event: MouseEvent): void {
    event.stopImmediatePropagation();
    event.preventDefault();
    if (this.productRef) {
      this.contextMenuService.openProductBinContextMenu(
        event,
        this.productRef.productId
      );
    }
  }

  ngOnInit() {
    this.subscriptions.add(
      this.productInspectorService.inspectedProduct$.subscribe((productId) => {
        this.isInspecting = this.productRef?.productId === productId;

        if (this.isInspecting) {
          this.elementRef.nativeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
          });
        }
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onMouseDown(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canEdit) return;
    if (this.productRef) {
      this.dragContextService.onMouseDownFromProductBin(this.productRef, event);
    }
  }

  // A tray/case variant is available when its dimensions are populated on the reference.
  get hasTray(): boolean {
    return this.productRef?.trayWidth != null && this.productRef?.trayHeight != null && this.productRef?.trayDepth != null;
  }

  get hasCase(): boolean {
    return this.productRef?.caseWidth != null && this.productRef?.caseHeight != null && this.productRef?.caseDepth != null;
  }

  // Starts a drag of the tray (1) or case (2) variant from the bin.
  onVariantMouseDown(event: MouseEvent, caseConfig: number) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canEdit) return;
    if (this.productRef) {
      this.dragContextService.onMouseDownFromProductBin(this.productRef, event, caseConfig);
    }
  }

  openInspector() {
    if (!this.dragContextService.isDraggingProduct() && this.productRef) {
      this.productInspectorService.inspect(this.productRef.productId);
    }
  }

  getProductStyle(dimensions: ProductDimensions | undefined) {
    if (!dimensions) return {};
    const zoom = this.zoomService.getCurrentZoom();
    return {
      width: `${zoom * dimensions.width}px`,
      height: `${zoom * dimensions.height}px`,
    };
  }

  getTotalOfProductOnPlanogram(productId: number | undefined): Observable<number> {
    return this.layoutService.layoutBreakdown$.pipe(
      map((breakdown) => (!productId ? 0 : breakdown.get(productId) || 0))
    );
  }

  isProductOnPlanogram(productId: number | undefined): Observable<boolean> {
    return this.layoutService.layoutBreakdown$.pipe(
      map((breakdown) => {
        if (!productId) return false;
        return (breakdown.get(productId) || 0) > 0;
      })
    );
  }
}